/**
 * Мастер создания объекта (направление F, роадмап): тип → организация →
 * сводка правил шаблона (кардинальность геометрий) → атрибуты черновика → POST /objects/with-card.
 */

import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { objectCardApi } from "@/services/card-api.service";
import { ObjectTypeKlsCombobox } from "@/components/admin/ObjectTypeKlsCombobox";
import { OrganizationKlsCombobox } from "@/components/admin/OrganizationKlsCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Info, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import type { CardValuePayload } from "@/types/card-template";

const STEPS = ["Тип и организация", "Шаблон и правила", "Атрибуты и создание"] as const;

export function ObjectCreationWizard() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [klsId, setKlsId] = useState<number | "">("");
  const [orgId, setOrgId] = useState<number | "">("");
  const [objectName, setObjectName] = useState("");

  const draftQuery = useQuery({
    queryKey: ["object-card-draft-wizard", klsId, orgId],
    queryFn: () =>
      objectCardApi.getCardDraft(Number(klsId), Number(orgId)),
    enabled: step >= 1 && klsId !== "" && orgId !== "",
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const draft = draftQuery.data;
      if (!draft || klsId === "" || orgId === "") {
        throw new Error("Нет данных черновика");
      }
      const name = objectName.trim() || "Новый объект";
      const baseValues = draft.object_values ?? [];
      const objNameOv = baseValues.find((ov) => ov.field_key === "obj_name");
      const qualFromDraft =
        objNameOv?.values?.[0]?.qual_id ??
        baseValues.flatMap((o) => o.values)[0]?.qual_id ??
        0;
      const object_values: CardValuePayload[] = [
        {
          field_key: "obj_name",
          qual_id: qualFromDraft || 1,
          value_text: name,
          kls_id: objNameOv?.values?.[0]?.kls_id ?? null,
        },
      ];
      return objectCardApi.createWithCard({
        kls_id: Number(klsId),
        organization_id: Number(orgId),
        object_values,
        geometries: [],
      });
    },
    onSuccess: (created) => {
      toast({ title: "Объект создан", description: `ID ${created.id}` });
      navigate("/");
    },
    onError: (e: Error) =>
      toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const canGoStep1 = klsId !== "" && orgId !== "";
  const tpl = draftQuery.data?.template;

  return (
    <div className="max-w-xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-2 text-lg font-semibold">
        <Sparkles className="h-5 w-5 text-primary" />
        Мастер создания объекта
      </div>
      <div className="flex gap-2 text-xs text-muted-foreground">
        {STEPS.map((label, i) => (
          <span
            key={label}
            className={i === step ? "text-foreground font-medium" : ""}
          >
            {i + 1}. {label}
          </span>
        ))}
      </div>

      {step === 0 && (
        <div className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Выберите тип объекта и организацию — по ним подбирается опубликованный шаблон карточки.
            </AlertDescription>
          </Alert>
          <div>
            <Label>Тип объекта (KLS, qual 354)</Label>
            <div className="mt-1.5">
              <ObjectTypeKlsCombobox value={klsId} onValueChange={setKlsId} />
            </div>
          </div>
          <div>
            <Label>Организация (KLS, qual 336)</Label>
            <div className="mt-1.5">
              <OrganizationKlsCombobox value={orgId} onValueChange={setOrgId} />
            </div>
          </div>
          <Button
            type="button"
            disabled={!canGoStep1}
            onClick={() => setStep(1)}
          >
            Далее
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <Button type="button" variant="ghost" size="sm" onClick={() => setStep(0)}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Назад
          </Button>
          {draftQuery.isLoading && (
            <p className="text-sm text-muted-foreground">Загрузка шаблона…</p>
          )}
          {draftQuery.data && !tpl && (
            <Alert variant="destructive">
              <AlertDescription className="text-xs">
                Опубликованный шаблон не найден для этой пары (тип, организация). Создайте и
                опубликуйте шаблон в админке.
              </AlertDescription>
            </Alert>
          )}
          {tpl && (
            <>
              <p className="text-sm">
                Шаблон: <strong>{tpl.name}</strong>{" "}
                <span className="text-muted-foreground font-mono text-xs">({tpl.code})</span>
              </p>
              <div>
                <Label className="text-xs">Правила кардинальности геометрий</Label>
                {tpl.geometry_rules?.length ? (
                  <ul className="mt-2 text-xs border rounded-md p-2 space-y-1 list-disc list-inside">
                    {tpl.geometry_rules.map((r) => (
                      <li key={r.id ?? `${r.geometry_semantic_kls_id}-${r.min_count}`}>
                        Семантика kls_id={r.geometry_semantic_kls_id}: min {r.min_count}, max{" "}
                        {r.max_count ?? "∞"}
                        {r.is_required ? ", обязательна" : ""}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">
                    Правила не заданы — ограничения только на уровне сервиса геометрий по умолчанию.
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                После создания объекта добавьте геометрии на карте; при необходимости мастер можно
                расширить пошаговым вводом геометрий здесь.
              </p>
            </>
          )}
          <Button
            type="button"
            disabled={!tpl || draftQuery.isLoading}
            onClick={() => setStep(2)}
          >
            Далее
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <Button type="button" variant="ghost" size="sm" onClick={() => setStep(1)}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Назад
          </Button>
          <div>
            <Label htmlFor="wiz-name">Название объекта</Label>
            <Input
              id="wiz-name"
              className="mt-1.5"
              value={objectName}
              onChange={(e) => setObjectName(e.target.value)}
              placeholder="Например: Здание №1"
            />
          </div>
          <Button
            type="button"
            disabled={createMutation.isPending || !canGoStep1}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? "Создание…" : "Создать объект"}
          </Button>
        </div>
      )}
    </div>
  );
}
